import type { ContentCard } from '@braze/react-native-sdk'
import { DesignSystem } from '..'
import { useCallback, useEffect } from 'react'
import * as cardMetrics from '@/src/services/Braze/contentCardMetrics'
import { ChevronRight } from '@/src/assets/icons/ChevronRight'
import { StyleSheet, View, type ViewProps, Text } from 'react-native'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import { Image } from 'expo-image'
import { useLogError } from '@/src/common/hooks/useLogError'
import { navigateToAppUrl } from './navigateToAppUrl'
import { typography } from '@/src/common/theme'

const styles = StyleSheet.create({
  card: { flex: 1 },
  cardContent: {
    flexDirection: 'row',
    flexGrow: 0.5,
    paddingTop: 16,
  },
  imageContainer: {
    aspectRatio: 1,
    width: 64,
    height: 64,
    marginRight: 8,
  },
  image: {
    width: '100%',
    height: '100%',
  },
  textContainer: {
    flex: 1,
  },
  title: typography['heading/heading3'],
  description: {
    flexGrow: 1,
    ...typography['paragraph/sm'],
  },
  chevronContainer: {
    marginLeft: 8,
    alignSelf: 'center',
  },
})

type ClassicCard = ContentCard & {
  type: 'Classic'
}

export default function ClassicCard({
  contentCard,
  minWidth,
  minHeight,
  style,
}: {
  contentCard: ClassicCard
  minWidth?: number | string
  minHeight?: number | string
  style?: ViewProps['style']
}) {
  const logError = useLogError()

  useEffect(() => {
    cardMetrics.logImpression(contentCard)
  }, [contentCard])

  const { title, cardDescription, url, openURLInWebView, image } = contentCard

  const pressHandler = useCallback(() => {
    if (!url) {
      return
    }

    cardMetrics.logClick(contentCard)

    if (openURLInWebView) {
      void openInAppBrowser(url, title, 'tab')
    } else {
      navigateToAppUrl({ navigationUrl: url, onError: logError })
    }
  }, [url, title, openURLInWebView, contentCard, logError])

  if (contentCard.isControl) {
    // Do not render control cards, log impressions only.
    return null
  }

  return (
    <DesignSystem.SplitCard
      onPress={url ? pressHandler : undefined}
      style={[styles.card, { minWidth, minHeight }, style]}
    >
      <DesignSystem.SplitCard.Content style={styles.cardContent}>
        {image && (
          <View style={styles.imageContainer}>
            <Image source={{ uri: image }} style={styles.image} />
          </View>
        )}
        <View style={styles.textContainer}>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.description}>{cardDescription}</Text>
        </View>

        {url && (
          <View style={styles.chevronContainer}>
            <ChevronRight />
          </View>
        )}
      </DesignSystem.SplitCard.Content>
    </DesignSystem.SplitCard>
  )
}
